import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './MonitGPS.css';
import { useLanguage } from './LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import T from './translations';

const ZawodnikLanding = () => {
  const { language } = useLanguage();
  const TT = T[language];
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 glass-effect"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <a href="/">
              <img src="/images/logominitgps.png" alt="MonitGPS Logo" className="h-12 w-auto cursor-pointer" />
            </a>
          </div>

          <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="/" className="hover:text-cyan-400 transition-colors">{TT.nav.home}</a>
            <a href="/elearning" className="hover:text-cyan-400 transition-colors text-cyan-500 font-bold">{TT.nav.elearning}</a>
            <LanguageSwitcher />
          </div>

          <div className="md:hidden flex items-center space-x-3">
            <LanguageSwitcher />
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-cyan-400 focus:outline-none">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0a]/95 border-b border-white/10 flex flex-col px-6 py-4 space-y-4 text-sm font-medium backdrop-blur-xl">
            <a href="/" className="hover:text-cyan-400">{TT.nav.home}</a>
            <a href="/elearning" className="text-cyan-500 font-bold hover:text-cyan-400">{TT.nav.elearning}</a>
          </div>
        )}
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-carbon">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>

        <motion.div
          className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-blue-500/15 rounded-full blur-[100px]"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.2, 0.4] }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-24">
          <motion.div
            className="inline-block mb-6 px-4 py-2 glass-effect rounded-full text-sm font-medium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="text-cyan-400">●</span> {TT.zawodnikLanding.badge}
          </motion.div>

          <motion.h1
            className="font-display font-black text-5xl md:text-7xl leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {TT.zawodnikLanding.title1} <span className="text-gradient">{TT.zawodnikLanding.title2}</span>
          </motion.h1>

          <motion.p
            className="text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {TT.zawodnikLanding.subtitle}
          </motion.p>

          <motion.div
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            {/* Rajdy Card */}
            <motion.a
              href="/zawodnik-rally"
              className="group relative block glass-effect rounded-3xl overflow-hidden cursor-pointer"
              onMouseEnter={() => setHoveredCard('rally')}
              onMouseLeave={() => setHoveredCard(null)}
              whileHover={{ scale: 1.03, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 border border-cyan-500/0 group-hover:border-cyan-500/40 rounded-3xl transition-all duration-500" />
              <div className="relative z-10 p-10 flex flex-col items-center text-center space-y-6">
                <motion.div
                  className="w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center text-5xl"
                  animate={hoveredCard === 'rally' ? { rotate: [0, -5, 5, 0] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  🚗
                </motion.div>
                <div>
                  <h2 className="font-display font-black text-4xl mb-3 group-hover:text-cyan-400 transition-colors duration-300">
                    {TT.zawodnikLanding.rallyTitle}
                  </h2>
                  <p className="text-gray-400 leading-relaxed">{TT.zawodnikLanding.rallyDesc}</p>
                </div>
                <div className="flex items-center space-x-2 text-cyan-400 font-display font-bold text-sm group-hover:translate-x-1 transition-transform duration-300">
                  <span>{TT.zawodnikLanding.rallyBtn}</span>
                  <span className="text-lg">→</span>
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-500" />
              </div>
            </motion.a>

            {/* Wyścig Górski Card */}
            <motion.a
              href="/zawodnik-climb"
              className="group relative block glass-effect rounded-3xl overflow-hidden cursor-pointer"
              onMouseEnter={() => setHoveredCard('climb')}
              onMouseLeave={() => setHoveredCard(null)}
              whileHover={{ scale: 1.03, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 border border-orange-500/0 group-hover:border-orange-500/40 rounded-3xl transition-all duration-500" />
              <div className="relative z-10 p-10 flex flex-col items-center text-center space-y-6">
                <motion.div
                  className="w-24 h-24 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center text-5xl"
                  animate={hoveredCard === 'climb' ? { rotate: [0, -5, 5, 0] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  ⛰️
                </motion.div>
                <div>
                  <h2 className="font-display font-black text-4xl mb-3 group-hover:text-orange-400 transition-colors duration-300">
                    {TT.zawodnikLanding.climbTitle}
                  </h2>
                  <p className="text-gray-400 leading-relaxed">{TT.zawodnikLanding.climbDesc}</p>
                </div>
                <div className="flex items-center space-x-2 text-orange-400 font-display font-bold text-sm group-hover:translate-x-1 transition-transform duration-300">
                  <span>{TT.zawodnikLanding.climbBtn}</span>
                  <span className="text-lg">→</span>
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-orange-500 to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-500" />
              </div>
            </motion.a>

            {/* Special Event Card */}
            <motion.a
              href="/event"
              className="group relative block glass-effect rounded-3xl overflow-hidden cursor-pointer"
              onMouseEnter={() => setHoveredCard('event')}
              onMouseLeave={() => setHoveredCard(null)}
              whileHover={{ scale: 1.03, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 border border-purple-500/0 group-hover:border-purple-500/40 rounded-3xl transition-all duration-500" />
              <div className="relative z-10 p-10 flex flex-col items-center text-center space-y-6">
                <motion.div
                  className="w-24 h-24 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center p-3"
                  animate={hoveredCard === 'event' ? { rotate: [0, -5, 5, 0] } : {}}
                  transition={{ duration: 0.5 }}
                >
                  <img src="https://prl.org.pl/wp-content/uploads/2026/05/logo.png" alt="Rajd Legend 2026" className="w-full h-full object-contain" />
                </motion.div>
                <div>
                  <h2 className="font-display font-black text-4xl mb-3 group-hover:text-purple-400 transition-colors duration-300">
                    {TT.zawodnikLanding.eventTitle}
                  </h2>
                  <p className="text-gray-400 leading-relaxed">{TT.zawodnikLanding.eventDesc}</p>
                </div>
                <div className="flex items-center space-x-2 text-purple-400 font-display font-bold text-sm group-hover:translate-x-1 transition-transform duration-300">
                  <span>{TT.zawodnikLanding.eventBtn}</span>
                  <span className="text-lg">→</span>
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-500" />
              </div>
            </motion.a>
          </motion.div>

          {/* E-learning CTA */}
          <motion.div
            className="mt-12 flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <a href="https://elearning.monitgps.pl" target="_blank" rel="noreferrer" className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-display font-bold text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center space-x-3 group">
              <span>{TT.zawodnikLanding.elearningBtn}</span>
              <span className="text-2xl group-hover:scale-110 transition-transform">🎓</span>
            </a>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 scan-line"></div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 glass-effect border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between text-sm text-gray-500">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <p>{TT.footer.copyright}</p>
            <a href="/polityka-prywatnosci" className="hover:text-cyan-400 transition-colors underline decoration-white/20 underline-offset-4">
              {TT.footer.privacyPolicy}
            </a>
          </div>
          <div className="mt-4 md:mt-0">
            <p>{TT.footer.tagline}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ZawodnikLanding;
